import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  HStack,
  Input,
  Link,
  Select,
  Stack,
  Switch,
  Text,
  useColorMode,
  useToast,
} from '@chakra-ui/react';
import {
  Bell,
  ChevronRight,
  Download,
  Phone,
  Smartphone,
  UserX,
  Volume2,
} from 'lucide-react';
import { useState } from 'react';
import { DeleteAccountSheet } from '../components/DeleteAccountSheet';
import {
  AboutSection,
  BlockedPeopleSection,
} from '../components/page/SafetySections';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  PageScaffold,
  Section,
  SettingRow,
} from '../components/page/PageScaffold';
import { useAtom } from 'jotai';
import { isAppleMobileTab } from '../lib/pwa';
import { problemKey } from '../lib/pushProblem';
import {
  alertSettingsAtom,
  blessingButtonAtom,
  blessingWordsAtom,
  photoQualityAtom,
  regionAtom,
  festivalTouchesAtom,
  type AlertSettings,
} from '../store';
import {
  MESSAGE_VIBRATION,
  playMessageSound,
  startRingtone,
  stopVibration,
  vibrate,
} from '../lib/sounds';
import { useAuth, useFamily, useInstallPrompt, usePush } from '../hooks';
import { appLocale, formatClock, formatDate as formatDay } from '../lib/format';
import {
  LANGUAGES,
  languageSettingAtom,
  t as translateNow,
  useT,
  type LanguageSetting,
} from '../i18n';

function formatDate(value: string | null): string {
  if (!value) return translateNow('account.settings.never');
  return new Date(value).toLocaleDateString(appLocale());
}

// Shows what each date style looks like (D145).
const SAMPLE = new Date(2026, 9, 22, 20, 56);
export function SettingsPage() {
  const { currentUser } = useAuth();
  const { families } = useFamily();
  const {
    support,
    problem,
    config,
    permission,
    subscribed,
    preferences,
    subscriptions,
    busy,
    error,
    enable,
    disable,
    updatePreferences,
    sendTest,
  } = usePush();
  const { available: installAvailable, prompt: promptInstall } =
    useInstallPrompt();
  const toast = useToast();

  const navigate = useNavigate();
  const { colorMode, toggleColorMode } = useColorMode();
  const [alertSettings, setAlertSettings] = useAtom(alertSettingsAtom);
  const [photoQuality, setPhotoQuality] = useAtom(photoQualityAtom);
  const [blessingButton, setBlessingButton] = useAtom(blessingButtonAtom);
  const [blessingWords, setBlessingWords] = useAtom(blessingWordsAtom);
  const [region, setRegion] = useAtom(regionAtom);
  const [festivalTouches, setFestivalTouches] = useAtom(festivalTouchesAtom);
  const [languageSetting, setLanguageSetting] = useAtom(languageSettingAtom);
  const t = useT();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const canVibrate = typeof navigator !== 'undefined' && 'vibrate' in navigator;

  const setAlert = (key: keyof AlertSettings, value: boolean) =>
    setAlertSettings({ ...alertSettings, [key]: value });

  const testMessageAlert = () => {
    if (alertSettings.messageSound) playMessageSound();
    if (alertSettings.vibration) vibrate(MESSAGE_VIBRATION);
  };

  const testRingtone = () => {
    const stop = startRingtone({
      sound: alertSettings.callRingtone,
      vibrate: alertSettings.vibration,
    });
    window.setTimeout(() => {
      stop();
      stopVibration();
    }, 4000);
  };

  const toggleMute = (familyId: string, muted: boolean) => {
    const next = muted
      ? [...preferences.mutedFamilyIds, familyId]
      : preferences.mutedFamilyIds.filter((id) => id !== familyId);
    void updatePreferences({ mutedFamilyIds: [...new Set(next)] });
  };

  const onTest = async () => {
    await sendTest();
    toast({ title: t('account.settings.testSent'), status: 'info' });
  };

  const notice = (
    status: 'info' | 'warning' | 'error',
    text: string,
    title?: string
  ) => (
    <Alert status={status} borderRadius="xl" mb={3} alignItems="flex-start">
      <AlertIcon />
      <Box>
        {title && <AlertTitle>{title}</AlertTitle>}
        <AlertDescription>{text}</AlertDescription>
      </Box>
    </Alert>
  );

  return (
    <PageScaffold
      title={t('account.settings.title')}
      subtitle={t('account.settings.subtitle')}
    >
      <Section title={t('account.settings.account')}>
        <SettingRow
          label={currentUser?.displayName}
          hint={`@${currentUser?.handle}`}
        >
          <Button
            h="40px"
            variant="outline"
            colorScheme="gray"
            borderColor="line"
            rightIcon={<ChevronRight size={16} />}
            onClick={() => navigate('/profile')}
          >
            {t('account.menu.profile')}
          </Button>
        </SettingRow>
        <SettingRow
          label={t('account.delete.row')}
          hint={t('account.delete.rowHint')}
        >
          <Button
            h="40px"
            variant="outline"
            colorScheme="red"
            leftIcon={<UserX size={16} />}
            onClick={() => setDeleteOpen(true)}
          >
            {t('account.delete.button')}
          </Button>
        </SettingRow>
      </Section>
      <DeleteAccountSheet
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
      />

      <BlockedPeopleSection />

      <Section title={t('account.settings.appearance')}>
        <SettingRow
          label={t('account.menu.darkMode')}
          hint={t('account.settings.darkModeHint')}
        >
          <Switch
            size="lg"
            isChecked={colorMode === 'dark'}
            onChange={toggleColorMode}
            aria-label={t('account.menu.darkMode')}
          />
        </SettingRow>
        {/* Festival mode (D312): on this device only, on by default. */}
        <SettingRow
          label={t('account.settings.festivalTouches')}
          hint={t('account.settings.festivalTouchesHint')}
        >
          <Switch
            size="lg"
            isChecked={festivalTouches}
            onChange={(event) => setFestivalTouches(event.target.checked)}
            aria-label={t('account.settings.festivalTouches')}
          />
        </SettingRow>
      </Section>

      <Section
        title={t('account.settings.notifications')}
        description={t('account.settings.notificationsDescription')}
      >
        {/* iOS only allows web push in the Home Screen app, so a Safari tab
            offers the install instead of a toggle that can't work (D185). */}
        {isAppleMobileTab() ? (
          <SettingRow
            label={t('account.settings.pushInstall')}
            hint={t('account.settings.pushInstallHint')}
          >
            <Button
              as={RouterLink}
              to="/install"
              h="40px"
              rightIcon={<ChevronRight size={16} />}
            >
              {t('account.settings.pushInstallHow')}
            </Button>
          </SettingRow>
        ) : (
          <>
            {/* Why, and the fix for this browser (D307). */}
            {support === 'unsupported' &&
              notice(
                'warning',
                t(problemKey(problem ?? 'unsupported')),
                t('account.settings.pushUnsupported')
              )}
            {support === 'supported' &&
              config &&
              !config.enabled &&
              notice('info', t('account.settings.pushNotConfigured'))}
            {support !== 'unsupported' &&
              permission === 'denied' &&
              notice('warning', t(problemKey('blocked')))}
            {error && notice('error', error)}

            <SettingRow
              label={
                subscribed
                  ? t('account.settings.pushOn')
                  : t('account.settings.pushOff')
              }
              hint={
                subscriptions.length === 0
                  ? t('account.settings.noDevices')
                  : subscriptions.length === 1
                    ? t('account.settings.devicesOne')
                    : t('account.settings.devicesOther', {
                        count: subscriptions.length,
                      })
              }
            >
              <Button
                h="40px"
                variant={subscribed ? 'outline' : 'solid'}
                colorScheme={subscribed ? 'gray' : 'brand'}
                borderColor="line"
                isLoading={busy}
                isDisabled={
                  support === 'unsupported' ||
                  !config?.enabled ||
                  permission === 'denied'
                }
                onClick={subscribed ? disable : enable}
              >
                {subscribed
                  ? t('account.settings.turnOff')
                  : t('account.settings.turnOn')}
              </Button>
            </SettingRow>
          </>
        )}

        {subscribed && (
          <>
            <SettingRow label={t('account.settings.messages')}>
              <Switch
                size="lg"
                aria-label={t('account.settings.messagesLabel')}
                isChecked={preferences.messages}
                onChange={(event) =>
                  void updatePreferences({ messages: event.target.checked })
                }
              />
            </SettingRow>
            <SettingRow
              label={t('account.header.calls')}
              hint={t('account.settings.callsHint')}
            >
              <Switch
                size="lg"
                aria-label={t('account.settings.callsLabel')}
                isChecked={preferences.calls ?? true}
                onChange={(event) =>
                  void updatePreferences({ calls: event.target.checked })
                }
              />
            </SettingRow>
            <SettingRow
              label={t('account.header.gatherings')}
              hint={t('account.settings.gatheringsHint')}
            >
              <Switch
                size="lg"
                aria-label={t('account.settings.gatheringsLabel')}
                isChecked={preferences.gatherings ?? true}
                onChange={(event) =>
                  void updatePreferences({ gatherings: event.target.checked })
                }
              />
            </SettingRow>
            <SettingRow
              label={t('account.settings.familyUpdates')}
              hint={t('account.settings.familyUpdatesHint')}
            >
              <Switch
                size="lg"
                aria-label={t('account.settings.familyUpdatesLabel')}
                isChecked={preferences.familyApps}
                onChange={(event) =>
                  void updatePreferences({ familyApps: event.target.checked })
                }
              />
            </SettingRow>
            {families.map((family) => (
              <SettingRow
                key={family.id}
                label={t('account.settings.muteFamily', { name: family.name })}
                hint={t('account.settings.muteFamilyHint')}
              >
                <Switch
                  size="lg"
                  aria-label={t('account.settings.muteFamily', {
                    name: family.name,
                  })}
                  isChecked={preferences.mutedFamilyIds.includes(family.id)}
                  onChange={(event) =>
                    toggleMute(family.id, event.target.checked)
                  }
                />
              </SettingRow>
            ))}
            {subscriptions.length > 0 && (
              <Box pt={3}>
                <Text fontSize="14px" fontWeight={700} color="muted" pb={1}>
                  {t('account.settings.pushDevices')}
                </Text>
                {subscriptions.map((subscription) => (
                  <HStack
                    key={subscription.id}
                    spacing={2}
                    py={1}
                    fontSize="14px"
                    color="muted"
                  >
                    <Smartphone size={14} aria-hidden="true" />
                    <Text flex="1" noOfLines={1}>
                      {subscription.userAgent ??
                        t('account.settings.unknownDevice')}
                    </Text>
                    <Text flexShrink={0}>
                      {t('account.settings.since', {
                        date: formatDate(subscription.createdAt),
                      })}
                    </Text>
                  </HStack>
                ))}
              </Box>
            )}
            <Button
              mt={3}
              h="44px"
              variant="outline"
              colorScheme="gray"
              borderColor="line"
              leftIcon={<Bell size={16} />}
              onClick={onTest}
            >
              {t('account.settings.sendTest')}
            </Button>
          </>
        )}
      </Section>

      <Section
        title={t('settings.language.title')}
        description={t('settings.language.description')}
      >
        <SettingRow
          label={t('settings.language.label')}
          hint={t('settings.language.hint')}
        >
          <Select
            w="auto"
            minW="150px"
            h="44px"
            aria-label={t('settings.language.label')}
            value={languageSetting}
            onChange={(event) =>
              setLanguageSetting(event.target.value as LanguageSetting)
            }
          >
            <option value="device">{t('settings.language.device')}</option>
            {LANGUAGES.map((language) => (
              <option key={language.code} value={language.code}>
                {language.name}
              </option>
            ))}
          </Select>
        </SettingRow>
        <SettingRow
          label={t('account.settings.indianDates')}
          hint={
            region === 'IN'
              ? t('account.settings.indianDatesOn', {
                  sample: `${formatDay(SAMPLE, 'en-IN')}, ${formatClock(SAMPLE, 'en-IN')}`,
                })
              : t('account.settings.indianDatesOff', {
                  sample: `${formatDay(SAMPLE, undefined)}, ${formatClock(SAMPLE, undefined)}`,
                })
          }
        >
          <Switch
            size="lg"
            aria-label={t('account.settings.indianDates')}
            isChecked={region === 'IN'}
            onChange={(event) =>
              setRegion(event.target.checked ? 'IN' : 'device')
            }
          />
        </SettingRow>
      </Section>

      <Section
        title={t('account.settings.photos')}
        description={t('account.settings.photosDescription')}
      >
        <SettingRow
          label={t('account.settings.originalQuality')}
          hint={
            photoQuality === 'original'
              ? t('account.settings.originalOn')
              : t('account.settings.originalOff')
          }
        >
          <Switch
            size="lg"
            aria-label={t('account.settings.originalQuality')}
            isChecked={photoQuality === 'original'}
            onChange={(event) =>
              setPhotoQuality(event.target.checked ? 'original' : 'standard')
            }
          />
        </SettingRow>
      </Section>

      {/* Aashirwad (D280): on this device, like drafts. */}
      <Section
        title={t('account.settings.blessings')}
        description={t('account.settings.blessingsDescription')}
      >
        <SettingRow
          label={t('account.settings.blessingButton')}
          hint={t('account.settings.blessingButtonHint')}
        >
          <Switch
            size="lg"
            aria-label={t('account.settings.blessingButton')}
            isChecked={blessingButton}
            onChange={(event) => setBlessingButton(event.target.checked)}
          />
        </SettingRow>
        <Box pt={3}>
          <Text as="label" htmlFor="blessing-words" fontWeight={600}>
            {t('account.settings.blessingWords')}
          </Text>
          <Input
            id="blessing-words"
            mt={2}
            h="48px"
            maxLength={120}
            value={blessingWords}
            placeholder={t('chat.bless.default')}
            onChange={(event) => setBlessingWords(event.target.value)}
          />
          <Text fontSize="14px" color="muted" mt={1}>
            {t('account.settings.blessingWordsHint')}
          </Text>
        </Box>
      </Section>

      <Section
        title={t('account.settings.sounds')}
        description={t('account.settings.soundsDescription')}
      >
        <SettingRow label={t('account.settings.messageSound')}>
          <Switch
            size="lg"
            aria-label={t('account.settings.messageSound')}
            isChecked={alertSettings.messageSound}
            onChange={(event) => setAlert('messageSound', event.target.checked)}
          />
        </SettingRow>
        <SettingRow label={t('account.settings.ringtone')}>
          <Switch
            size="lg"
            aria-label={t('account.settings.ringtone')}
            isChecked={alertSettings.callRingtone}
            onChange={(event) => setAlert('callRingtone', event.target.checked)}
          />
        </SettingRow>
        <SettingRow
          label={t('account.settings.vibrate')}
          hint={canVibrate ? undefined : t('account.settings.cantVibrate')}
        >
          <Switch
            size="lg"
            aria-label={t('account.settings.vibrate')}
            isDisabled={!canVibrate}
            isChecked={canVibrate && alertSettings.vibration}
            onChange={(event) => setAlert('vibration', event.target.checked)}
          />
        </SettingRow>
        <HStack spacing={2} pt={3} flexWrap="wrap">
          <Button
            h="44px"
            variant="outline"
            colorScheme="gray"
            borderColor="line"
            leftIcon={<Volume2 size={16} />}
            onClick={testMessageAlert}
          >
            {t('account.settings.testSound')}
          </Button>
          <Button
            h="44px"
            variant="outline"
            colorScheme="gray"
            borderColor="line"
            leftIcon={<Phone size={16} />}
            onClick={testRingtone}
          >
            {t('account.settings.testRingtone')}
          </Button>
        </HStack>
        <Text fontSize="14px" color="muted" pt={3}>
          {t('account.settings.closedSounds')}
        </Text>
      </Section>

      <Section
        title={t('account.settings.install')}
        description={t('account.settings.installDescription')}
      >
        {installAvailable ? (
          <Button
            h="48px"
            leftIcon={<Download size={18} />}
            onClick={() => void promptInstall()}
          >
            {t('account.settings.install')}
          </Button>
        ) : (
          <Stack spacing={1} fontSize="15px" color="ink.body">
            <Link as={RouterLink} to="/install" color="accent" fontWeight={700}>
              {t('account.settings.installGuide')}
            </Link>
            <Text>{t('account.settings.installIphone')}</Text>
            {isAppleMobileTab() && (
              <Text color="accent" fontWeight={600} pt={1}>
                {t('account.settings.installAppleTab')}
              </Text>
            )}
            <Text>{t('account.settings.installDesktop')}</Text>
          </Stack>
        )}
      </Section>

      <AboutSection />
    </PageScaffold>
  );
}
